import type { Translate } from '@/lib/labels';

/**
 * The Python engine reports terminal failures as an English `error_message`. Most
 * server text can't be translated client-side, but this particular set is small,
 * fixed, and the FIRST thing a producer reads when a run dies — so it is worth
 * matching and localising.
 *
 * Matching is on a distinctive substring rather than the whole sentence, so a
 * reworded message on the engine side degrades to showing the server text verbatim
 * instead of silently rendering the wrong translation.
 *
 * Source: engine/server.py (`error_message` / `emitter.fail`).
 */
const KNOWN: { match: RegExp; key: string }[] = [
  { match: /no final video was rendered/i, key: 'engineError.noFinalVideo' },
  { match: /resume returned without a final video/i, key: 'engineError.resumeNoVideo' },
  { match: /did not produce any assets/i, key: 'engineError.noAssets' },
  { match: /project has no script/i, key: 'engineError.noScript' },
  { match: /superseded by a newer production/i, key: 'engineError.superseded' },
];

/**
 * Localise a known engine error, or return it unchanged.
 *
 * Returning the raw string for anything unrecognised is deliberate: an untranslated
 * diagnostic is far more useful than a generic "something went wrong".
 */
export function localizeEngineError(t: Translate, message?: string | null): string {
  if (!message) return '';
  const hit = KNOWN.find((k) => k.match.test(message));
  return hit ? t(hit.key) : message;
}
